import { db, CallFoundryDexie } from '../data/db';
import type { DiagnosticLogEntry, DiagnosticLogLevel } from '../data/models';

const MAX_JOURNAL_ENTRIES = 100;

function generateUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Scrubs potential PII from strings and metadata objects
 * Removes emails, phone numbers, 16-digit credit cards, authorization headers
 */
export function scrubPII(text: string): string {
  return text
    .replace(/[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+/g, '[SCRUBBED_EMAIL]')
    .replace(/\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/g, '[SCRUBBED_PHONE]')
    .replace(/\b(?:\d{4}[-\s]?){3}\d{4}\b/g, '[SCRUBBED_CARD]')
    .replace(/Bearer\s+[A-Za-z0-9-_=.]+/gi, 'Bearer [SCRUBBED_TOKEN]');
}

export function scrubMetadata(metadata: Record<string, unknown>): Record<string, unknown> {
  const scrubbed: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(metadata)) {
    if (typeof value === 'string') {
      scrubbed[key] = scrubPII(value);
    } else if (typeof value === 'object' && value !== null) {
      try {
        scrubbed[key] = JSON.parse(scrubPII(JSON.stringify(value)));
      } catch {
        scrubbed[key] = '[COMPLEX_OBJECT]';
      }
    } else {
      scrubbed[key] = value;
    }
  }
  return scrubbed;
}

export class DiagnosticJournal {
  private dexie: CallFoundryDexie;

  constructor(databaseInstance: CallFoundryDexie = db) {
    this.dexie = databaseInstance;
  }

  async log(
    level: DiagnosticLogLevel,
    context: string,
    message: string,
    metadata: Record<string, unknown> = {}
  ): Promise<DiagnosticLogEntry> {
    const entry: DiagnosticLogEntry = {
      id: generateUUID(),
      timestamp: new Date().toISOString(),
      level,
      context: scrubPII(context),
      message: scrubPII(message),
      metadata: scrubMetadata(metadata),
    };

    try {
      await this.dexie.diagnostics.put(entry);

      // Enforce bounded journal size (max 100 entries)
      const count = await this.dexie.diagnostics.count();
      if (count > MAX_JOURNAL_ENTRIES) {
        const oldest = await this.dexie.diagnostics
          .orderBy('timestamp')
          .limit(count - MAX_JOURNAL_ENTRIES)
          .toArray();
        const idsToDelete = oldest.map((o) => o.id);
        await this.dexie.diagnostics.bulkDelete(idsToDelete);
      }
    } catch {
      // Fallback if IndexedDB write fails
      console.warn('[DiagnosticJournal] Failed to persist log entry:', entry);
    }

    return entry;
  }

  info(context: string, message: string, metadata?: Record<string, unknown>): Promise<DiagnosticLogEntry> {
    return this.log('info', context, message, metadata);
  }

  warn(context: string, message: string, metadata?: Record<string, unknown>): Promise<DiagnosticLogEntry> {
    return this.log('warn', context, message, metadata);
  }

  error(context: string, message: string, metadata?: Record<string, unknown>): Promise<DiagnosticLogEntry> {
    return this.log('error', context, message, metadata);
  }

  async getRecentLogs(limit = 50): Promise<DiagnosticLogEntry[]> {
    return this.dexie.diagnostics.orderBy('timestamp').reverse().limit(limit).toArray();
  }

  async clear(): Promise<void> {
    await this.dexie.diagnostics.clear();
  }
}

export const diagnosticJournal = new DiagnosticJournal();
